import React, { useState, useEffect, useCallback, useRef } from "react";
import { makeStyles } from "@mui/styles";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from "@mui/material/Autocomplete";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ImageIcon from "@mui/icons-material/Image";
import LockIcon from "@mui/icons-material/Lock";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SendIcon from "@mui/icons-material/Send";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import QuoteDialog from "../../components/QuoteDialog";
import QuotePrint from "../../components/QuotePrint";
import SendToChatDialog from "../../components/SendToChatDialog";

const STATUS = {
  pendente:  { label: "Pendente",  color: "#f37021" },
  aceito:    { label: "Aceito",    color: "#388e3c" },
  recusado:  { label: "Recusado",  color: "#d32f2f" },
};

const useStyles = makeStyles((theme) => ({
  root:     { padding: theme.spacing(2) },
  toolbar:  { display:"flex", gap:8, alignItems:"center", marginBottom:16, flexWrap:"wrap" },
  title:    { display:"flex", alignItems:"center", gap:8, marginBottom:16 },
  table:    { minWidth:700 },
  chip:     { fontWeight:600, fontSize:11 },
}));

const fmtDate  = (d) => d ? new Date(d + "T12:00:00").toLocaleDateString("pt-BR") : "—";
const fmtMoney = (v) => v != null ? Number(v).toLocaleString("pt-BR", { style:"currency", currency:"BRL" }) : "—";
const fmtPM    = (pm) => { if (!pm) return "—"; try { const p = JSON.parse(pm); return p.descricao || pm; } catch { return pm; } };

const isExpired = (q) => {
  if (!q.validUntil || q.status !== "pendente") return false;
  return new Date(q.validUntil + "T23:59:59") < new Date();
};

const Quotes = () => {
  const classes = useStyles();
  const [quotes, setQuotes] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("todas");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [printQuote, setPrintQuote] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [converting, setConverting] = useState(null);
  const [convertDialog, setConvertDialog] = useState(null); // quote sendo convertida
  const [convertPmMethods, setConvertPmMethods] = useState([]);
  const [convertPmLoading, setConvertPmLoading] = useState(false);
  const [convertPmError, setConvertPmError] = useState(null);
  const [convertPayForm, setConvertPayForm] = useState({ methodId: "", methodName: "", discount: "", installments: 1 });
  const [sendDialog, setSendDialog] = useState(null); // quote a enviar

  // Dialog de aceite com comprovante
  const [acceptDialog, setAcceptDialog] = useState(null); // quote sendo aceito
  const [acceptMode, setAcceptMode] = useState("imagem"); // "imagem" | "codigo"
  const [acceptFile, setAcceptFile] = useState(null);
  const [acceptCode, setAcceptCode] = useState("");
  const [accepting, setAccepting] = useState(false);
  const fileInputRef = useRef();

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quotes", {
        params: { status: statusFilter, search, limit: 200 },
      });
      setQuotes(data.rows || []);
      setCount(data.count || 0);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(fetchQuotes, search ? 400 : 0);
    return () => clearTimeout(t);
  }, [fetchQuotes, search]);

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir este orçamento?")) return;
    setDeleting(id);
    try {
      await api.delete(`/quotes/${id}`);
      toast.success("Orçamento excluído");
      fetchQuotes();
    } catch (err) {
      toastError(err);
    } finally {
      setDeleting(null);
    }
  };

  const openConvertDialog = async (q) => {
    setConvertDialog(q);
    setConvertPayForm({ methodId: "", methodName: "", discount: "", installments: 1 });
    setConvertPmError(null);
    setConvertPmLoading(true);
    try {
      const { data } = await api.get("/bling/payment-methods");
      setConvertPmMethods(Array.isArray(data) ? data : []);
    } catch {
      setConvertPmError("Não foi possível carregar as formas de pagamento.");
      setConvertPmMethods([]);
    } finally {
      setConvertPmLoading(false);
    }
  };

  const handleConvert = async (q) => { openConvertDialog(q); };

  const handleConfirmConvert = async () => {
    const q = convertDialog;
    if (!q) return;
    setConvertDialog(null);
    setConverting(q.id);
    try {
      const { data } = await api.post(`/quotes/${q.id}/convert-to-os`, {
        paymentMethodId: convertPayForm.methodId ? Number(convertPayForm.methodId) : undefined,
        paymentMethodName: convertPayForm.methodName || undefined,
        discount: convertPayForm.discount ? parseFloat(String(convertPayForm.discount)) : 0,
        installmentsCount: convertPayForm.installments > 1 ? Number(convertPayForm.installments) : 1,
      });
      toast.success(`OS ${data.numero} criada com sucesso!`);
      fetchQuotes();
    } catch (err) {
      toastError(err);
    } finally {
      setConverting(null);
    }
  };

  const handleStatusChange = async (q, status) => {
    try {
      await api.put(`/quotes/${q.id}`, { status });
      toast.success(`Status alterado para "${STATUS[status]?.label}"`);
      fetchQuotes();
    } catch (err) {
      toastError(err);
    }
  };

  const openAcceptDialog = (q) => {
    setAcceptDialog(q);
    setAcceptMode("imagem");
    setAcceptFile(null);
    setAcceptCode("");
  };

  const handleAcceptConfirm = async () => {
    if (acceptMode === "imagem" && !acceptFile) {
      toast.error("Selecione uma imagem do comprovante");
      return;
    }
    if (acceptMode === "codigo" && !acceptCode.trim()) {
      toast.error("Informe o código de pagamento");
      return;
    }
    setAccepting(true);
    try {
      const form = new FormData();
      if (acceptMode === "imagem") form.append("paymentProof", acceptFile);
      else form.append("paymentCode", acceptCode.trim());
      await api.post(`/quotes/${acceptDialog.id}/accept`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Orçamento aceito com sucesso!");
      setAcceptDialog(null);
      fetchQuotes();
    } catch (err) {
      toastError(err);
    } finally {
      setAccepting(false);
    }
  };

  const handleSaved = () => {
    setDialogOpen(false);
    setEditingQuote(null);
    fetchQuotes();
  };

  return (
    <div className={classes.root}>
      <div className={classes.title}>
        <RequestQuoteIcon style={{ color:"#F37021", fontSize:28 }} />
        <Typography variant="h5" style={{ fontWeight:700 }}>Orçamentos</Typography>
        <Typography variant="body2" color="textSecondary">({count} {count === 1 ? "orçamento" : "orçamentos"})</Typography>
      </div>

      <div className={classes.toolbar}>
        <TextField
          size="small" variant="outlined"
          placeholder="Buscar número, cliente..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ minWidth:260 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")} edge="end">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Select size="small" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          variant="outlined" style={{ minWidth:160 }}>
          <MenuItem value="todas">Todos os status</MenuItem>
          {Object.entries(STATUS).map(([k, v]) => (
            <MenuItem key={k} value={k}>{v.label}</MenuItem>
          ))}
        </Select>
        <div style={{ flex:1 }} />
        <Button variant="contained" startIcon={<AddIcon />}
          style={{ backgroundColor:"#F37021", color:"#fff" }}
          onClick={() => { setEditingQuote(null); setDialogOpen(true); }}>
          Novo orçamento
        </Button>
      </div>

      <Paper variant="outlined">
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ fontWeight:700 }}>Nº</TableCell>
              <TableCell style={{ fontWeight:700 }}>Cliente</TableCell>
              <TableCell style={{ fontWeight:700 }}>Total</TableCell>
              <TableCell style={{ fontWeight:700 }}>Pagamento</TableCell>
              <TableCell style={{ fontWeight:700 }}>Válido até</TableCell>
              <TableCell style={{ fontWeight:700 }}>Status</TableCell>
              <TableCell style={{ fontWeight:700 }}>Bling</TableCell>
              <TableCell style={{ fontWeight:700 }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" style={{ padding:32 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : quotes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" style={{ padding:32, color:"#999" }}>
                  Nenhum orçamento encontrado.
                </TableCell>
              </TableRow>
            ) : quotes.map(q => {
              const expired = isExpired(q);
              return (
                <TableRow key={q.id} style={{ opacity: q.status === "recusado" ? 0.6 : 1 }}>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight:700, fontFamily:"monospace" }}>
                      {q.numero}
                    </Typography>
                    {q.serviceOrderId && (
                      <Typography variant="caption" style={{ color:"#388e3c" }}>→ OS criada</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight:600 }}>{q.clientName || "—"}</Typography>
                    {q.clientPhone && (
                      <Typography variant="caption" style={{ color:"#888" }}>{q.clientPhone}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight:700 }}>{fmtMoney(q.total)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{fmtPM(q.paymentMethod)}</Typography>
                  </TableCell>
                  <TableCell>
                    <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                      <Typography variant="body2">{fmtDate(q.validUntil)}</Typography>
                      {expired && (
                        <Tooltip title="Orçamento vencido">
                          <WarningAmberIcon style={{ fontSize:14, color:"#f57c00" }} />
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Chip
                      className={classes.chip}
                      size="small"
                      label={STATUS[q.status]?.label || q.status}
                      style={{ backgroundColor: STATUS[q.status]?.color, color:"#fff" }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" style={{ color:"#888", fontSize:10 }}>
                      {q.serviceOrderId ? "OS criada" : "Ao converter em OS"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <div style={{ display:"flex", gap:2 }}>
                      {/* Imprimir */}
                      <Tooltip title="Imprimir / Ver PDF">
                        <IconButton size="small" onClick={() => setPrintQuote(q)}>
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {/* Enviar no chat */}
                      <Tooltip title="Enviar no chat">
                        <IconButton size="small" onClick={() => setSendDialog(q)} style={{ color:"#1565c0" }}>
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {/* Editar */}
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => { setEditingQuote(q); setDialogOpen(true); }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {/* Duplicar */}
                      <Tooltip title="Duplicar orçamento">
                        <IconButton size="small" onClick={() => {
                          const copy = {
                            ...q,
                            id: undefined,
                            numero: undefined,
                            status: "pendente",
                            blingOrderId: null,
                            blingOrderNumero: null,
                            serviceOrderId: null,
                          };
                          setEditingQuote(copy);
                          setDialogOpen(true);
                        }} style={{ color:"#7b1fa2" }}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {/* Aceitar */}
                      {q.status === "pendente" && (
                        <Tooltip title="Marcar como aceito">
                          <IconButton size="small" onClick={() => openAcceptDialog(q)}
                            style={{ color:"#388e3c" }}>
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Recusar */}
                      {q.status === "pendente" && (
                        <Tooltip title="Marcar como recusado">
                          <IconButton size="small" onClick={() => handleStatusChange(q, "recusado")}
                            style={{ color:"#d32f2f" }}>
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Converter em OS */}
                      {(q.status === "aceito" || q.status === "pendente") && !q.serviceOrderId && (
                        <Tooltip title="Converter em Ordem de Serviço">
                          <IconButton size="small" onClick={() => handleConvert(q)}
                            disabled={converting === q.id}
                            style={{ color:"#7b1fa2" }}>
                            {converting === q.id ? <CircularProgress size={14} /> : <SwapHorizIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Excluir */}
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => handleDelete(q.id)}
                          disabled={deleting === q.id}
                          style={{ color:"#d32f2f" }}>
                          {deleting === q.id ? <CircularProgress size={14} /> : <DeleteIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {dialogOpen && (
        <QuoteDialog
          open={dialogOpen}
          onClose={() => { setDialogOpen(false); setEditingQuote(null); }}
          onSaved={handleSaved}
          quote={editingQuote}
        />
      )}

      {printQuote && (
        <QuotePrint quote={printQuote} onClose={() => setPrintQuote(null)} />
      )}

      {sendDialog && (
        <SendToChatDialog
          open
          onClose={() => setSendDialog(null)}
          title={`Enviar ${sendDialog.numero} no chat`}
          subtitle={sendDialog.clientName ? `Cliente: ${sendDialog.clientName}` : undefined}
          ticketId={sendDialog.ticketId || null}
          sendEndpoint={(tid) => `/quotes/ticket/${tid}/send/${sendDialog.id}`}
        />
      )}

      {/* Dialog conversão em OS com dados de pagamento */}
      <Dialog open={Boolean(convertDialog)} onClose={() => setConvertDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 8 }}>
          <SwapHorizIcon style={{ color: "#7b1fa2" }} />
          Converter em OS
          {convertDialog && (
            <Typography variant="body2" color="textSecondary" style={{ marginLeft: 4 }}>
              — {convertDialog.numero}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent style={{ paddingTop: 8 }}>
          <Typography variant="body2" style={{ marginBottom: 16, color: "#555" }}>
            Informe os dados de pagamento para a OS a ser criada.
          </Typography>

          {convertDialog && (
            <Typography variant="body2" style={{ marginBottom: 12, color: "#555" }}>
              Total do orçamento: <strong>
                {Number(convertDialog.total || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
              </strong>
            </Typography>
          )}

          {convertPmLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
              <CircularProgress size={24} style={{ color: "#7b1fa2" }} />
            </div>
          ) : convertPmError ? (
            <>
              <Typography variant="body2" color="error" style={{ marginBottom: 10 }}>{convertPmError}</Typography>
              <TextField
                label="Forma de pagamento (texto livre)"
                size="small" fullWidth variant="outlined"
                value={convertPayForm.methodName}
                onChange={e => setConvertPayForm(f => ({ ...f, methodName: e.target.value, methodId: "" }))}
                style={{ marginBottom: 16 }}
              />
            </>
          ) : (
            <Autocomplete
              options={convertPmMethods}
              getOptionLabel={opt => opt.descricao || opt.nome || ""}
              value={convertPmMethods.find(m => String(m.id) === String(convertPayForm.methodId)) || null}
              onChange={(_, val) => setConvertPayForm(f => ({
                ...f,
                methodId: val ? String(val.id) : "",
                methodName: val ? (val.descricao || val.nome || "") : "",
              }))}
              renderInput={params => (
                <TextField {...params} label="Forma de pagamento" variant="outlined" size="small" fullWidth />
              )}
              noOptionsText="Nenhuma forma encontrada"
              style={{ marginBottom: 16 }}
            />
          )}

          <TextField
            label="Desconto (R$)"
            size="small" fullWidth variant="outlined" type="number"
            inputProps={{ min: 0, step: 0.01 }}
            value={convertPayForm.discount}
            onChange={e => setConvertPayForm(f => ({ ...f, discount: e.target.value }))}
            style={{ marginBottom: 16 }}
            helperText={convertPayForm.discount > 0 && convertDialog
              ? `Líquido: ${(Number(convertDialog.total || 0) - parseFloat(convertPayForm.discount || 0)).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}`
              : undefined
            }
          />

          <TextField
            label="Número de parcelas"
            size="small" fullWidth variant="outlined" select
            value={convertPayForm.installments}
            onChange={e => setConvertPayForm(f => ({ ...f, installments: Number(e.target.value) }))}
          >
            {[1,2,3,4,5,6,8,10,12].map(n => (
              <MenuItem key={n} value={n}>{n === 1 ? "À vista" : `${n}x`}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions style={{ padding: "8px 24px 16px" }}>
          <Button onClick={() => setConvertDialog(null)} style={{ textTransform: "none" }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmConvert}
            style={{ backgroundColor: "#7b1fa2", color: "#fff", textTransform: "none" }}
            startIcon={<SwapHorizIcon />}
          >
            Criar OS
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog aceite com comprovante */}
      <Dialog open={Boolean(acceptDialog)} onClose={() => !accepting && setAcceptDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar aceite — {acceptDialog?.numero}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            Anexe o comprovante de pagamento ou informe o código da transação.
          </Typography>
          <ToggleButtonGroup
            value={acceptMode}
            exclusive
            onChange={(_, v) => { if (v) { setAcceptMode(v); setAcceptFile(null); setAcceptCode(""); } }}
            size="small"
            fullWidth
            style={{ marginBottom: 16 }}
          >
            <ToggleButton value="imagem" style={{ flex: 1, gap: 6 }}>
              <ImageIcon fontSize="small" /> Imagem
            </ToggleButton>
            <ToggleButton value="codigo" style={{ flex: 1, gap: 6 }}>
              <LockIcon fontSize="small" /> Código / Senha
            </ToggleButton>
          </ToggleButtonGroup>

          {acceptMode === "imagem" ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={e => setAcceptFile(e.target.files[0] || null)}
              />
              <Button
                variant="outlined"
                startIcon={<ImageIcon />}
                onClick={() => fileInputRef.current.click()}
                fullWidth
              >
                {acceptFile ? acceptFile.name : "Selecionar imagem"}
              </Button>
              {acceptFile && (
                <img
                  src={URL.createObjectURL(acceptFile)}
                  alt="preview"
                  style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 4, objectFit: "contain" }}
                />
              )}
            </div>
          ) : (
            <TextField
              label="Código / Senha de pagamento"
              fullWidth
              value={acceptCode}
              onChange={e => setAcceptCode(e.target.value)}
              placeholder="Ex: código PIX, N° transação..."
              autoFocus
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcceptDialog(null)} disabled={accepting}>Cancelar</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleAcceptConfirm}
            disabled={accepting || (acceptMode === "imagem" ? !acceptFile : !acceptCode.trim())}
            startIcon={accepting ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            Confirmar aceite
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Quotes;
